import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useGallery } from './GalleryLayout.jsx';
import GalleryBar from './GalleryBar.jsx';
import Lightbox from './Lightbox.jsx';
import { buildRows } from './justify.js';

/**
 * The Photographs page.
 *
 * Layout is a justified row grid (see justify.js): uniform height per row,
 * true aspect ratios, no cropping. On phones it collapses to one full-width
 * photo per row.
 *
 * Built for ~600 images, so the page renders in batches and reveals the next as
 * a sentinel scrolls into view — the justified rows are computed only over what
 * is currently shown, while the lightbox still spans the whole set.
 */
const BATCH = 60;
const GAP = 36; // matches the --jgap in styles.css

export default function PhotosPage() {
  const { slug, gallery, photos, videos, sections, downloadsOn, openDownload, downloadOne } = useGallery();
  const [day, setDay] = useState(null); // null = every day
  const [sectionId, setSectionId] = useState(null); // null = everything in the current day
  const [visible, setVisible] = useState(BATCH);
  const [lightbox, setLightbox] = useState(-1);
  const [width, setWidth] = useState(0);
  const gridRef = useRef(null);
  const sentinel = useRef(null);
  const empty = photos.length === 0;

  // Distinct days in section order. One day (or none) means the photographer
  // never split this gallery across days, so the day row would just be a single
  // dead tab — the chips stand on their own.
  const days = useMemo(() => {
    const seen = [];
    sections.forEach((s) => {
      if (s.day_name && !seen.includes(s.day_name)) seen.push(s.day_name);
    });
    return seen;
  }, [sections]);
  const showDays = days.length > 1;

  const chips = useMemo(
    () => (showDays && day ? sections.filter((s) => s.day_name === day) : sections),
    [sections, showDays, day],
  );

  const counts = useMemo(() => {
    const m = new Map();
    photos.forEach((p) => {
      if (p.section_id) m.set(p.section_id, (m.get(p.section_id) || 0) + 1);
    });
    return m;
  }, [photos]);

  /*
   * What the grid and the lightbox both work from. Unfiled photographs have no
   * section, so they surface under "All" and nowhere else — which means turning
   * this feature on never hides anything from a couple, it only adds ways in.
   */
  const filtered = useMemo(() => {
    if (sectionId) return photos.filter((p) => p.section_id === sectionId);
    if (day) {
      const ids = new Set(sections.filter((s) => s.day_name === day).map((s) => s.id));
      return photos.filter((p) => ids.has(p.section_id));
    }
    return photos;
  }, [photos, sections, day, sectionId]);

  // A new filter is a new grid: start the batching over, and never leave the
  // lightbox pointing at an index from the previous set.
  useEffect(() => {
    setVisible(BATCH);
    setLightbox(-1);
  }, [day, sectionId]);

  // The row maths needs the real content width, so measure it and re-measure on
  // resize / orientation change.
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    // Must be the CONTENT width, not clientWidth — clientWidth includes the
    // horizontal padding, and laying rows out at that width overflows the box
    // by exactly the padding and gives the page a horizontal scrollbar.
    const style = getComputedStyle(el);
    const pad = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    setWidth(el.clientWidth - pad);
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [empty]);

  // Reveal the next batch as the sentinel nears the viewport. Declared before the
  // empty-gallery redirect so hooks stay unconditional (Rules of Hooks).
  useEffect(() => {
    if (empty || visible >= filtered.length) return;
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible((v) => Math.min(v + BATCH, filtered.length));
      },
      { rootMargin: '1200px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible, filtered.length, empty]);

  const shown = useMemo(() => filtered.slice(0, visible), [filtered, visible]);
  const rows = useMemo(() => buildRows(shown, width, GAP), [shown, width]);

  // Photographs rise gently into place as they scroll into view. One observer
  // covers every cell, and each is unobserved once revealed, so a 600-photo
  // gallery costs one observer rather than six hundred listeners. Cells within
  // a row are staggered slightly so a row arrives as a sweep, not a slab.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const cells = grid.querySelectorAll('.jcell:not(.in)');
    if (!cells.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.05 },
    );
    cells.forEach((cell) => io.observe(cell));
    return () => io.disconnect();
  }, [rows]);

  if (empty) return <Navigate to={`/${slug}/film`} replace />;

  // Full-bleed hero photo above the heading — the gallery's cover if it's a
  // photo, otherwise the opening frame.
  const cover = photos.find((p) => p.id === gallery.cover_asset_id);
  const hero = cover || photos[0];

  return (
    <div className="gal photos-page">
      <GalleryBar slug={slug} videos={videos} photos={photos} downloadsOn={downloadsOn} onDownload={openDownload} />

      <main className="photos-main">
        {hero && (
          <div className="page-hero">
            <img src={hero.src || hero.thumb} alt="" />
            <span className="page-hero-veil" />
          </div>
        )}

        <div className="section-head" style={{ paddingTop: hero ? 'clamp(2.5rem, 6vw, 4rem)' : 'calc(var(--bar-h) + 2.5rem)' }}>
          <span className="label">
            {filtered.length === photos.length
              ? `${photos.length} Photographs`
              : `${filtered.length} of ${photos.length} Photographs`}
          </span>
          <div className="section-rule" />
          <h2 className="serif">Photographs</h2>
        </div>

        {sections.length > 0 && (
          <div className="filters">
            {showDays && (
              <div className="filter-row filter-days">
                <button className={day ? '' : 'on'} onClick={() => { setDay(null); setSectionId(null); }}>
                  Everything
                </button>
                {days.map((d) => (
                  <button
                    key={d}
                    className={day === d ? 'on' : ''}
                    onClick={() => { setDay(d); setSectionId(null); }}
                  >
                    {d}
                  </button>
                ))}
              </div>
            )}
            <div className="filter-row filter-chips">
              <button className={sectionId ? '' : 'on'} onClick={() => setSectionId(null)}>
                All
              </button>
              {chips.map((s) => (
                <button key={s.id} className={sectionId === s.id ? 'on' : ''} onClick={() => setSectionId(s.id)}>
                  {s.name}
                  <span className="chip-n">{counts.get(s.id) || 0}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="jgrid" ref={gridRef}>
          {rows.map((row, ri) => (
            <div className="jrow" key={ri} style={{ height: `${row.height}px` }}>
              {row.items.map(({ photo, index, width: w }, ci) => (
                <button
                  key={photo.id}
                  className="jcell"
                  style={{ width: `${w}px`, '--stagger': `${ci * 70}ms` }}
                  onClick={() => setLightbox(index)}
                  aria-label={`Open photo ${index + 1}`}
                >
                  <Thumb photo={photo} />
                </button>
              ))}
            </div>
          ))}
        </div>

        {visible < filtered.length && (
          <div ref={sentinel} className="photo-sentinel">
            <span className="label">Loading more · {visible} of {filtered.length}</span>
          </div>
        )}
      </main>

      {lightbox >= 0 && (
        // Scoped to the filter on purpose: arrowing through "Decor" should stay
        // in Decor rather than wandering into the rest of the wedding.
        <Lightbox
          photos={filtered}
          index={lightbox}
          onIndex={setLightbox}
          onClose={() => setLightbox(-1)}
          allowDownload={gallery.allow_photo_download}
          onDownload={downloadOne}
        />
      )}
    </div>
  );
}

function Thumb({ photo }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={photo.thumb || photo.src}
      alt={photo.filename}
      loading="lazy"
      decoding="async"
      className={loaded ? 'loaded' : ''}
      onLoad={() => setLoaded(true)}
    />
  );
}
